import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid,
} from "recharts";
import { getCandles, fmtNum, fmtPrice } from "@/lib/api";
import { CandlestickChart as CandleIcon } from "lucide-react";

const GREEN = "#00C805";
const RED = "#FF3B30";

// Custom candlestick: floating bar from low->high with body for open->close
const Candle = (props) => {
  const { x, width, y, height, payload } = props;
  if (!payload) return null;
  const { open, close, high, low } = payload;
  const range = high - low || 1;
  const up = close >= open;
  const color = up ? GREEN : RED;
  const scale = height / range; // px per price unit; y=high top, y+height=low bottom
  const yFor = (p) => y + (high - p) * scale;
  const bodyTop = yFor(Math.max(open, close));
  const bodyH = Math.max(1, Math.abs(yFor(open) - yFor(close)));
  const cx = x + width / 2;
  const bw = Math.max(2, width * 0.6);
  return (
    <g>
      <line x1={cx} y1={y} x2={cx} y2={y + height} stroke={color} strokeWidth={1} />
      <rect x={cx - bw / 2} y={bodyTop} width={bw} height={bodyH} fill={color} rx={0.5} />
    </g>
  );
};

const TT = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-app border border-border rounded p-2 text-[11px] font-mono space-y-0.5">
      <div className="text-tertiary">{d.label}</div>
      <div className="text-secondary">O <span className="text-white">{fmtPrice(d.open)}</span> H <span className="text-white">{fmtPrice(d.high)}</span></div>
      <div className="text-secondary">L <span className="text-white">{fmtPrice(d.low)}</span> C <span className={d.close >= d.open ? "text-bull" : "text-bear"}>{fmtPrice(d.close)}</span></div>
      <div className="text-secondary">Vol <span className="text-white">{fmtNum(d.volume)}</span></div>
      <div className="text-secondary">OI <span className="text-ai">{fmtNum(d.oi)}</span></div>
    </div>
  );
};

const RANGES = [
  { id: 40, label: "1D" },
  { id: 78, label: "1D+" },
  { id: 120, label: "2D" },
];

export const TickerChart = ({ symbol }) => {
  const [count, setCount] = useState(78);
  const { data, isLoading } = useQuery({
    queryKey: ["candles", symbol, count],
    queryFn: () => getCandles(symbol, 5, count),
    refetchInterval: 30000,
    enabled: !!symbol,
  });

  if (!symbol) return null;
  const candles = data?.candles || [];
  const lows = candles.map((c) => c.low);
  const highs = candles.map((c) => c.high);
  const yMin = lows.length ? Math.min(...lows) * 0.998 : 0;
  const yMax = highs.length ? Math.max(...highs) * 1.002 : 1;
  const withRange = candles.map((c) => ({ ...c, range: [c.low, c.high] }));

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden" data-testid="ticker-chart">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <CandleIcon size={15} className="text-ai" />
          <h4 className="text-sm font-semibold uppercase tracking-[0.1em] text-white">{symbol} · Intraday</h4>
        </div>
        <div className="flex gap-1">
          {RANGES.map((r) => (
            <button key={r.id} data-testid={`chart-range-${r.label}`} onClick={() => setCount(r.id)}
              className={`text-[10px] font-mono px-2 py-1 rounded ${count === r.id ? "bg-neutral text-white" : "text-secondary hover:bg-surface-2"}`}>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="h-64 flex items-center justify-center text-tertiary font-mono text-xs">Loading chart…</div>
      ) : (
        <div className="p-2">
          {/* Price candlesticks */}
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={withRange} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke="#1A1A1D" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "#71717A", fontSize: 9, fontFamily: "IBM Plex Mono" }} interval={Math.floor(candles.length / 6)} axisLine={{ stroke: "#222225" }} tickLine={false} />
              <YAxis domain={[yMin, yMax]} orientation="right" tick={{ fill: "#71717A", fontSize: 9, fontFamily: "IBM Plex Mono" }} width={52} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v.toFixed(v >= 100 ? 0 : 1)}`} />
              <Tooltip content={<TT />} cursor={{ stroke: "#333", strokeDasharray: "3 3" }} />
              <Bar dataKey="range" shape={<Candle />} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>

          {/* Volume */}
          <ResponsiveContainer width="100%" height={64}>
            <ComposedChart data={candles} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <XAxis dataKey="label" hide />
              <YAxis orientation="right" width={52} tick={{ fill: "#71717A", fontSize: 9, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtNum(v)} />
              <Tooltip content={<TT />} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="volume" isAnimationActive={false}>
                {candles.map((c, i) => (
                  <Cell key={i} fill={c.close >= c.open ? "rgba(0,200,5,0.5)" : "rgba(255,59,48,0.5)"} />
                ))}
              </Bar>
            </ComposedChart>
          </ResponsiveContainer>

          {/* Open Interest */}
          <ResponsiveContainer width="100%" height={64}>
            <ComposedChart data={candles} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <XAxis dataKey="label" hide />
              <YAxis orientation="right" width={52} tick={{ fill: "#71717A", fontSize: 9, fontFamily: "IBM Plex Mono" }} axisLine={false} tickLine={false} tickFormatter={(v) => fmtNum(v)} />
              <Tooltip content={<TT />} cursor={{ stroke: "#333", strokeDasharray: "3 3" }} />
              <Line dataKey="oi" stroke="#00F0FF" strokeWidth={1.5} dot={false} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
          <div className="flex items-center justify-end gap-4 px-3 pb-1 text-[9px] font-mono text-tertiary uppercase">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-bull inline-block" /> Volume</span>
            <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-ai inline-block" /> Open Interest</span>
          </div>
        </div>
      )}
    </div>
  );
};
